import {useSnackbar} from "notistack";
import React from 'react';
import {ApiContext} from '..';
import {useNotification} from "../../components/App/hooks";
import {DataType, Mutation, Query} from "./types";

export interface ApiProviderProps {
  apiUrl: string;
  apiKey: string;
  onError?(errors: any[], path: string): void;
}


export const ApiProvider: React.FC<ApiProviderProps> = ({
  apiUrl,
  apiKey,
  children,
  onError,
}) => {
    const [tempCache, setTempCache] = React.useState<{ [key: string]: any }>({});

    return (
      <ApiContext.Provider value={{
        fetchQuery<T>(query: Query<T>, cachePolicy = "network-first"): Promise<DataType<T>> {
          const strQry = JSON.stringify(query);
          if (["network-first"].includes(cachePolicy) && tempCache.hasOwnProperty(strQry)) {
            return Promise.resolve({data: tempCache[strQry], errors: []});
          }
          const targetPath = apiUrl + query.path;

          return fetch(targetPath, {
            headers: {
              'Content-Type': "application/json",
	      'Authorization': apiKey,
              'X-Fields': query.xFields?.join(',') ?? "",
            },
          }).then(async response => {
            if (response.status >= 400 && response.status <= 599) {
              throw await response.json() || "Something went wrong " + response.status;
            }
            return response.json().catch(e => {
              console.warn("There was an error" +
                           " while trying to parse the response to a" +
                           " JSON object; no status code with error was found.", e);
            });
          }).then((response) => {
            setTempCache(prev => ({
              ...prev,
              [strQry]: response,
            }));
            return {
              data: response,
              errors: [],
            };
          }).catch(err => {
            const errors = [err];
            onError?.(errors, targetPath);
            return {
              data: null,
              errors,
            };
          });
        },
        mutate<T, D>(mutation: Mutation<T>, data: D): Promise<DataType<T>> {
          const targetPath = apiUrl + mutation.path;
          return fetch(targetPath, {
            headers: {
              'Content-Type': "application/json",
	      'Authorization': apiKey,
              'X-Fields': mutation.xFields?.join(',') ?? "",
              Accept: "application/json",
            },
            body: data ? JSON.stringify(data) : undefined,
            method: mutation.type,
          }).then(async response => {
            if (response.status >= 400 && response.status <= 599) {
              throw await response.json() || "Something went wrong " + response.status;
            }
            return response.json().catch(e => {
              console.warn("There was an error" +
                           " while trying to parse the response to a" +
                           " JSON object; no status code with error was found.", e);
            });
          }).then((response) => {
            return {
              data: response,
              errors: [],
            };
          }).catch(err => {
            const errors = [err];
            onError?.(errors, targetPath);
            return {
              data: null,
              errors,
            };
          });
        },

      }}>
        {children}
      </ApiContext.Provider>
    );
};
